import React, { useEffect, useRef, useState } from 'react';
import { CloseRegularIcon } from 'src/monet/icons';
import { useHistory } from 'react-router-dom';
import { useNewJourneyStore } from '../store/new-journey';
import { useUpdateEffect } from 'react-use';
import { apiGetJourneyTemplateCategories, apiGetOrganizationTemplates } from 'src/utils/journeyApi';
import { Journey } from 'src/common/interfaces/journey.interface';
import { generateJourneyShareUrl } from 'src/utils/journey';
import { NeueLoadingSpinner } from 'src/editor/content-creation/components/loading/spinner';
import { useSpring, useSpringRef, animated } from '@react-spring/web';
import classNames from 'classnames';
import { useCurrentOrganization } from 'src/store/organization';


export type TemplateType = 'template' | 'organization-template';

const Template = ({ journey, onClickUse, type }: { journey: Journey; onClickUse: () => void; type: TemplateType}) => {
  const { currentOrganization } = useCurrentOrganization();
  const organization = {
    slug: 'jny-templates'
  };

  if (type === 'organization-template') {
    organization.slug = currentOrganization.slug;
  }

  return (
    <div className="flex flex-col gap-2 text-neue-journey-fg rounded-lg w-[288px]">
      <div className="relative">
        <img src={journey.thumbnail_url!} className="w-full rounded-lg h-[162px]" alt="" />
        <div className="absolute inset-0 group">
          <div
            className="absolute inset-0 rounded-lg pointer-events-none transition-colors group-hover:bg-bedrock-black/[0.08]" />
          <div
            className="h-full w-full flex gap-2 items-center justify-center transition opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 text-neue-journey-medium-strong text-neue-journey-bg">
            <div onClick={onClickUse} className="bg-neue-journey-fg rounded-lg py-[9px] px-4 cursor-pointer">
              Use
            </div>
            <a
              href={generateJourneyShareUrl(organization, journey)}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-neue-journey-fg rounded-lg py-3 px-4 cursor-pointer"
            >
              <svg className="w-4- h-4 text-inherit" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M13.8 8C13.8 8.8 12.4 13 8.00001 13C3.60001 13 2.20001 8.8 2.20001 8C2.20001 7.2 3.60001 3 8.00001 3C12.4 3 13.8 7.2 13.8 8Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.00001 9.8C8.99412 9.8 9.80001 8.99411 9.80001 8C9.80001 7.00588 8.99412 6.2 8.00001 6.2C7.0059 6.2 6.20001 7.00588 6.20001 8C6.20001 8.99411 7.0059 9.8 8.00001 9.8Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </div>
      <span className="text-neue-journey-medium">{journey.name}</span>
    </div>
  );
};

const FLOAT_ANIMATION_CONFIG = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export const CreateJourneyTemplates = ({ onClose, type }: { onClose: () => void, type: TemplateType }) => {
  const isLoadingRef = useRef(true);
  const [category, setCategory] = useState<any>({ templates: [] });
  const history = useHistory();
  const { journeyTemplate, setJourneyTemplate, createJourney } = useNewJourneyStore((state) => ({
    setJourneyTemplate: state.setJourneyTemplate,
    journeyTemplate: state.journeyTemplate,
    createJourney: () => state.createJourney(history),
  }));

  useEffect(() => {
    const fetchTemplates = async () => {
      if (type === 'organization-template') {
        const { templates } = await apiGetOrganizationTemplates();

        isLoadingRef.current = false;

        return { templates: templates };
      }

      const { journey_template_categories } = await apiGetJourneyTemplateCategories();

      isLoadingRef.current = false;
      const [category] = journey_template_categories.filter((category: any) => !!category?.templates.length);

      return category;
    };

    fetchTemplates().then(setCategory);
  }, [type]);

  useUpdateEffect(() => {
    createJourney();
  }, [journeyTemplate]);

  const categoryAnimationApi = useSpringRef();
  const [categoryAnimationStyle] = useSpring(() => ({
    ref: categoryAnimationApi,
    from: { opacity: 0 },
    to: { opacity: 1 },
  }));

  const loadingAnimationApi = useSpringRef();
  const [loadingAnimationStyle] = useSpring(() => ({
    ref: loadingAnimationApi,
    from: { opacity: 1 },
    to: { opacity: 0 },
  }));

  useUpdateEffect(() => {
    loadingAnimationApi.start({ opacity: 0, config: FLOAT_ANIMATION_CONFIG });
    categoryAnimationApi.start({ opacity: 1, config: FLOAT_ANIMATION_CONFIG, delay: 200 });
  }, [isLoadingRef.current]);

  return (
    <>
      <div className="absolute top-0 right-0">
        <CloseRegularIcon
          className="w-5 h-5 m-6 hover:opacity-80 cursor-pointer text-inherit transition-colors"
          onClick={onClose}
        />
      </div>
      <div className={classNames('flex w-full', { 'h-full': isLoadingRef.current })}>
        {isLoadingRef.current ? (
          <animated.div style={loadingAnimationStyle} className="m-auto">
            <NeueLoadingSpinner theme="journey" size="16" />
          </animated.div>
        ) : (
          <animated.div className="flex flex-row gap-6 flex-wrap" style={categoryAnimationStyle}>
            {category?.templates.map((template: any) => {
              return (
                <Template
                  onClickUse={() => {
                    setJourneyTemplate(template);
                  }}
                  journey={template.journey}
                  type={type}
                />
              );
            })}
          </animated.div>
        )}
      </div>
    </>
  );
};
